import { router } from "@inertiajs/vue3";
import { showFullPageLoading } from "~vue/components/FullPageLoading.vue";
import { marked } from "marked";

import { noImageRenderer, sanitizeForChat } from "./utils/markdown";

export function openUrl(url: string, { target, queryParams }: { target?: string; queryParams?: Record<string, string> } = {}) {
    const finalUrl = formatUrl(url, { queryParams });

    if (target === "_blank") {
        window.open(finalUrl, target);
        return;
    }
    if (isInIFrame()) {
        showFullPageLoading();
        if (target) {
            window.open(finalUrl, target);
        } else {
            window.location.href = finalUrl;
        }
    } else {
        router.visit(finalUrl);
    }
}

/**
 * checks if a current page is loaded in an iframe
 * used as a quick way to check if we are in a microsoft teams instance
 * if we ever load our app into an iframe for another reason this needs to be reworked
 *
 * @returns {boolean} true if the current page is loaded in an iframe
 */
export const isInIFrame = () => {
    return window.self !== window.top;
};

/**
 * Checks if the app is running inside Microsoft Teams.
 * Handles both desktop (iframe) and mobile (iOS webview where isInIFrame returns false).
 * Uses the Teams JS SDK to reliably detect the Teams host environment.
 * The result is cached so multiple components share a single SDK initialization.
 */
let _isInTeamsPromise: Promise<boolean> | null = null;

export function isInTeams(): Promise<boolean> {
    if (isInIFrame()) {
        return Promise.resolve(true);
    }
    if (!_isInTeamsPromise) {
        _isInTeamsPromise = import("@microsoft/teams-js").then(({ app }) => app.initialize().then(() => true)).catch(() => false);
    }
    return _isInTeamsPromise;
}

export function toMarkdown(content: string) {
    const html = marked(content, { extensions: { renderers: noImageRenderer, childTokens: {} } });
    return sanitizeForChat(html);
}

/**
 * Creates a chat URL with the appropriate chat source parameter
 * @param {string} baseUrl - The base URL for the chat
 * @param {object} queryParams - The query parameters to add to the URL
 * @returns {string} The complete URL with query parameters attached
 */
export function formatUrl(baseUrl: string, { queryParams }: { queryParams?: Record<string, string> }) {
    let formattedUrl: URL;
    try {
        formattedUrl = new URL(baseUrl);
    } catch {
        formattedUrl = new URL(baseUrl, window.location.origin);
    }

    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            formattedUrl.searchParams.set(key, value);
        });
    }

    return formattedUrl.toString();
}

/**
 * A simple invariant function that throws an error in development if the condition is not met
 * and logs an error in production.
 *
 * @param condition is the boolean expected to be true
 * @param message is produced when condition is false
 * @returns
 */
export function invariant(condition: boolean, message: string) {
    if (condition) {
        return;
    }
    if (import.meta.env.DEV) {
        throw new Error(message);
    } else {
        console.error(message);
    }
}
